import { useState, useEffect, useLayoutEffect } from 'react';
import classes from './ProviderConnectionsErrorManager.module.css';
import PropTypes from 'prop-types';
import useProviderConnectionActions from 'Hooks/useProviderConnectionActions';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import RequiresSignatureForm from './RequiresSignatureForm';
import { createProviderConnection } from 'Services/providersConnections';
import useProviderConnectionsCredentials from 'Hooks/useProviderConnectionsCredentials';
import Button from 'Components/Atoms/Button/Button';
import { useTranslation } from 'react-i18next';

export default function RequiresSignature(props) {
    const { info, onClose, onEnableClose, onClearConnection, providerConnection } = props;
    const [canceling, setCanceling] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [credentials, setCredentials] = useState(null);
    const { get } = useProviderConnectionsCredentials();
    const { checkConnection } = useProviderConnectionActions();
    const { showUnknownError } = useErrors();

    const { t } = useTranslation('providers');

    useEffect(() => {
        onEnableClose();
    }, []);

    useLayoutEffect(() => {
        const storageCredentials = get(providerConnection.id);
        if (storageCredentials) {
            setCredentials(storageCredentials);
        }
    }, []);

    const handleCancel = async () => {
        setCanceling(true);
        await onClearConnection(false);

        setCanceling(false);
        onClose();
    };

    const handleSubmitFormWithCredentials = async (data) => {
        setSubmitting(true);

        try {
            onClearConnection();
            const conexionId = await createProviderConnection({
                connectorId: providerConnection.provider_connector_id,
                providerConnectionId: providerConnection.provider_connection_reference_id,
                name: providerConnection.name,
                accountId: info.account_id,
                counterId: info.counter_id,
                sessionId: info.session_id,
                otp: info.otp,
                ...data,
            });

            checkConnection({
                id: conexionId,
                logo_url: providerConnection.connector.logo_url,
                name: providerConnection.connector.name,
            });
        } catch (error) {
            showUnknownError(error);
        } finally {
            setSubmitting(false);
            onClose();
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);

        try {
            onClearConnection();
            const conexionId = await createProviderConnection({
                connectorId: providerConnection.provider_connector_id,
                providerConnectionId: providerConnection.provider_connection_reference_id,
                name: providerConnection.name,
                accountId: info.account_id,
                counterId: info.counter_id,
                sessionId: info.session_id,
                documentType: info.document_type,
                otp: info.otp,
                credentials,
            });

            checkConnection({
                id: conexionId,
                logo_url: providerConnection.connector.logo_url,
                name: providerConnection.connector.name,
                credentials,
            });
        } catch (error) {
            showUnknownError(error);
        } finally {
            setSubmitting(false);
            onClose();
        }
    };

    const {
        fields_types,
        document_types,
        fields_descriptions,
        fields_names = [],
    } = providerConnection.connector.service_provider_meta;
    const documentIndex = Number.isNaN(Number(info.document_type)) ? 0 : Number(info.document_type);
    const documentType = Array.isArray(document_types) ? document_types[documentIndex] : null;

    const fieldsNamesWithContent = Array.isArray(info.credentials)
        ? fields_names.filter((_name, index) => info.credentials[index] != null)
        : [];

    const disabledFields = ['document_type', ...fieldsNamesWithContent];

    if (credentials) {
        return (
            <div className="text-center">
                <h4>{t('requires-signature-title')}</h4>
                <div className="text-gray-500 mb-6">{t('requires-signature-description')}</div>

                <div className="pt-2 flex flex-col gap-2 justify-center items-center">
                    <Button variant="primary" onClick={handleSubmit} isBusy={submitting}>
                        <img alt="" src="/img/icon-padlock.svg" />
                        <span className="ms-2">{t('requires-signature-button')}</span>
                    </Button>
                    <button className={classes.textButton} type="button" onClick={handleCancel} disabled={canceling}>
                        {t('cancel-connection')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-12">
                <h4>{t('requires-signature-title')}</h4>
                <div className="text-gray-500 mb-6">{t('requires-signature-description-2')}</div>
                <div className={classes.divider} />
            </div>
            <RequiresSignatureForm
                onSubmit={handleSubmitFormWithCredentials}
                fieldsNames={fields_names}
                fieldsDescriptions={fields_descriptions}
                fieldsTypes={fields_types}
                documentTypes={document_types}
                documentType={documentType}
                fieldValues={info.credentials}
                disabledFields={disabledFields}
                onCancel={handleCancel}
                canceling={canceling}
                submitting={submitting}
            />
        </div>
    );
}

RequiresSignature.propTypes = {
    providerConnection: PropTypes.object.isRequired,
    description: PropTypes.string.isRequired,
    info: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onEnableClose: PropTypes.func.isRequired,
    onClearConnection: PropTypes.func.isRequired,
};
